import { db, extractModule, extractVesselId } from './db';
import { checkQuotaBeforeWrite, estimateObjectSize } from './quotaMonitor';

/**
 * Read JSON from IndexedDB with fallback
 * ASYNC version of localStorage readJSON
 */
export const readJSON = async (key, fallback = null) => {
  try {
    const record = await db.storage.get(key);
    return record ? record.value : fallback;
  } catch (error) {
    console.error('[StorageIDB] Read error:', key, error);
    return fallback;
  }
};

/**
 * Write JSON to IndexedDB AND emit event for sync
 * CRITICAL: Must emit same event as localStorage version
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {boolean} silent - If true, skip event emission (used in parallel mode to avoid double events)
 * @param {string} [source] - FT-457: provenance tag threaded onto the sr:storage:set
 *        event detail. 'cloud-hydrate' marks a download/hydration echo so the
 *        UniversalSyncBridge FT-68 justPulled cooldown can drop ONLY that echo
 *        and never a genuine user write. Genuine writes leave this undefined.
 */
export const writeJSON = async (key, value, silent = false, source = undefined) => {
  try {
    // Check quota before writing (safety check)
    const estimatedSize = estimateObjectSize(value);
    const quotaCheck = await checkQuotaBeforeWrite(estimatedSize);

    if (!quotaCheck.allowed) {
      console.error('[StorageIDB] Quota exceeded, write blocked:', quotaCheck.reason);
      throw new Error(`QuotaExceeded: ${quotaCheck.reason}`);
    }

    // Log warning if approaching quota
    if (quotaCheck.quotaInfo && quotaCheck.quotaInfo.status === 'warning') {
      console.warn('[StorageIDB] Quota warning:', quotaCheck.quotaInfo.percentUsed * 100 + '% used');
    }

    await db.storage.put({
      key: key,
      vesselId: extractVesselId(key),
      module: extractModule(key),
      timestamp: Date.now(),
      value: value
    });

    // CRITICAL: Emit event for UniversalSyncBridge (same as localStorage)
    // Skip if silent=true (parallel mode - localStorage already emitted)
    if (!silent) {
      window.dispatchEvent(new CustomEvent('sr:storage:set', {
        detail: {
          module: extractModule(key),
          vesselId: extractVesselId(key),
          key: key,
          value: value,
          timestamp: Date.now(),
          // FT-457: only present when the write is a cloud download/hydration
          // echo. handleStorageSet drops this ONLY inside the justPulled
          // cooldown; genuine writes (source undefined) are NEVER dropped.
          ...(source !== undefined ? { source } : {})
        }
      }));
    }

    return true;
  } catch (error) {
    console.error('[StorageIDB] Write error:', key, error);
    return false;
  }
};

/**
 * Remove item from IndexedDB
 */
export const removeItem = async (key) => {
  try {
    await db.storage.delete(key);
    return true;
  } catch (error) {
    console.error('[StorageIDB] Remove error:', key, error);
    return false;
  }
};

/**
 * Check if key exists in IndexedDB
 */
export const hasKey = async (key) => {
  try {
    const record = await db.storage.get(key);
    return !!record;
  } catch (error) {
    console.error('[StorageIDB] hasKey error:', key, error);
    return false;
  }
};

/**
 * Get all keys matching pattern
 */
export const getKeys = async (pattern) => {
  try {
    const allRecords = await db.storage.toArray();
    const keys = allRecords.map(r => r.key);

    if (!pattern) return keys;

    return keys.filter(key => key.includes(pattern));
  } catch (error) {
    console.error('[StorageIDB] getKeys error:', pattern, error);
    return [];
  }
};

/**
 * Clear all storage (use with caution)
 */
export const clearAll = async () => {
  try {
    await db.storage.clear();
    return true;
  } catch (error) {
    console.error('[StorageIDB] clearAll error:', error);
    return false;
  }
};

/**
 * Get storage size info
 */
export const getStorageInfo = async () => {
  try {
    const allRecords = await db.storage.toArray();
    let totalSize = 0;
    const items = {};

    allRecords.forEach(record => {
      const size = new Blob([JSON.stringify(record.value)]).size;
      totalSize += size;
      items[record.key] = size;
    });

    return {
      totalSize,
      itemCount: allRecords.length,
      items
    };
  } catch (error) {
    console.error('[StorageIDB] getStorageInfo error:', error);
    return { totalSize: 0, itemCount: 0, items: {} };
  }
};
